import { useState, useEffect } from 'react'
import { ModalLayout } from './ui/ModalLayout'
import api from '../services/api'

/**
 * BoosterModal - Modal for selecting and using booster items
 * 
 * Props:
 * - isOpen: Boolean - Control modal visibility
 * - onClose: Function - Called when modal closes
 * - actionType: String - 'scavenging' | 'crafting' | 'gatherings'
 * - runId: String - ID of the current run/job to boost
 * - currentRemaining: Number - Remaining seconds (for time-based actions)
 * - onBoostSuccess: Function - Called when boost is successful
 */
export function BoosterModal({
    isOpen,
    onClose,
    actionType,
    runId,
    currentRemaining = 0,
    onBoostSuccess
}) {
    const [boosters, setBoosters] = useState([])
    const [loading, setLoading] = useState(false)
    const [boosting, setBoosting] = useState(false)
    const [error, setError] = useState(null)
    const [selectedBooster, setSelectedBooster] = useState(null)

    // Fetch player's booster items when modal opens
    useEffect(() => {
        if (isOpen && actionType) {
            fetchBoosters()
        }
    }, [isOpen, actionType])

    // Normalize action type to match booster config (gathering -> gatherings)
    const normalizedActionType = actionType === 'gathering' ? 'gatherings' : actionType

    const fetchBoosters = async () => {
        setLoading(true)
        setError(null)
        try {
            // Correct endpoint is /inventory/, not /inventory/items
            const response = await api.get('/inventory/', {
                params: { item_type: 'booster' }
            })

            // Filter boosters that can be used for this action type
            const allBoosters = response.data?.items || response.data || []
            const validBoosters = allBoosters.filter(item => {
                const config = item.booster_config
                if (!config) return false
                return config.target_actions?.includes(normalizedActionType)
            })

            setBoosters(validBoosters)
        } catch (err) {
            console.error('Failed to fetch boosters:', err)
            setError('Failed to load boosters')
            setBoosters([])
        } finally {
            setLoading(false)
        }
    }

    const handleBoost = async (booster) => {
        if (boosting) return

        setBoosting(true)
        setError(null)

        try {
            let endpoint
            let body = { item_id: booster.item_id }

            // Use normalizedActionType for API routing
            switch (normalizedActionType) {
                case 'scavenging':
                    endpoint = '/scavenging/run/boost'
                    break
                case 'crafting':
                    endpoint = `/crafting/job/${runId}/boost`
                    break
                case 'gatherings':
                    endpoint = `/gathering/run/${runId}/boost`
                    break
                default:
                    throw new Error('Unknown action type')
            }

            const response = await api.post(endpoint, body)

            // Show success and close
            if (onBoostSuccess) {
                onBoostSuccess(response.data)
            }
            onClose()
        } catch (err) {
            console.error('Boost failed:', err)
            setError(err.response?.data?.detail || 'Failed to apply boost')
        } finally {
            setBoosting(false)
        }
    }

    const formatTime = (seconds) => {
        if (seconds >= 3600) {
            const hours = Math.floor(seconds / 3600)
            const mins = Math.floor((seconds % 3600) / 60)
            return `${hours}h ${mins}m`
        } else if (seconds >= 60) {
            const mins = Math.floor(seconds / 60)
            const secs = seconds % 60
            return `${mins}m ${secs}s`
        }
        return `${seconds}s`
    }

    const getBoostDescription = (booster) => {
        const config = booster.booster_config
        if (!config) return ''

        if (config.instant_complete) {
            return 'Instant Complete!'
        }

        if (normalizedActionType === 'gatherings' && config.ticks_processed > 0) {
            const suffix = config.skip_hunger ? ' (no hunger cost)' : ''
            return `+${config.ticks_processed} ticks${suffix}`
        }

        if (config.time_reduction_seconds > 0) {
            const reduction = formatTime(config.time_reduction_seconds)
            const wouldWaste = config.time_reduction_seconds > currentRemaining

            if (wouldWaste && currentRemaining > 0) {
                return `−${reduction} ⚠️ Overkill (${formatTime(currentRemaining)} left)`
            }
            return `−${reduction}`
        }

        return 'Boost'
    }

    const getRarityColor = (rarity) => {
        const colors = {
            common: 'border-gray-500 bg-gray-500/10',
            uncommon: 'border-green-500 bg-green-500/10',
            rare: 'border-blue-500 bg-blue-500/10',
            epic: 'border-purple-500 bg-purple-500/10',
            legendary: 'border-yellow-500 bg-yellow-500/10',
        }
        return colors[rarity] || colors.common
    }

    const actionLabels = {
        scavenging: 'Scavenging Run',
        crafting: 'Crafting Job',
        gatherings: 'Gathering'
    }

    return (
        <ModalLayout
            isOpen={isOpen}
            onClose={onClose}
            title="Use Booster"
            icon="⚡"
            maxWidth="md"
            footerContent={
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-tertiary hover:bg-bg-tertiary/80 rounded-md transition-colors"
                >
                    Cancel
                </button>
            }
        >
            <div className="space-y-4">
                {/* Action Info */}
                <div className="text-sm text-text-secondary">
                    {normalizedActionType === 'gatherings' ? (
                        <span>Select a booster to instantly process gathering ticks</span>
                    ) : (
                        <>
                            Select a booster to speed up your {actionLabels[normalizedActionType] || 'action'}
                            {currentRemaining > 0 && (
                                <span className="ml-2 text-text-primary">
                                    ({formatTime(currentRemaining)} remaining)
                                </span>
                            )}
                        </>
                    )}
                </div>

                {/* Error Display */}
                {error && (
                    <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-2 rounded-md text-sm">
                        {error}
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rarity-rare"></div>
                    </div>
                )}

                {/* No Boosters Available */}
                {!loading && boosters.length === 0 && (
                    <div className="text-center py-8 text-text-secondary">
                        <span className="text-3xl mb-2 block">📦</span>
                        <p>No boosters available for this action</p>
                        <p className="text-xs mt-1 text-text-muted">
                            Boosters can be obtained from vendors, daily rewards, or achievements
                        </p>
                    </div>
                )}

                {/* Booster List */}
                {!loading && boosters.length > 0 && (
                    <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
                        {boosters.map((booster) => (
                            <button
                                key={booster.player_item_id || booster.id}
                                onClick={() => handleBoost(booster)}
                                disabled={boosting}
                                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${getRarityColor(booster.rarity)
                                    } hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl">⚡</span>
                                        <div>
                                            <div className="font-medium text-text-primary">
                                                {booster.name}
                                            </div>
                                            <div className="text-xs text-text-secondary">
                                                {getBoostDescription(booster)}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {booster.quantity > 1 && (
                                            <span className="text-xs bg-bg-tertiary px-2 py-1 rounded text-text-secondary">
                                                x{booster.quantity}
                                            </span>
                                        )}
                                        {boosting ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        ) : (
                                            <span className="text-lg">→</span>
                                        )}
                                    </div>
                                </div>

                                {/* Life-saving badge for scavenging */}
                                {normalizedActionType === 'scavenging' && booster.booster_config?.can_save_life && (
                                    <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                                        <span>💚</span> Can save your life
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </ModalLayout>
    )
}

export default BoosterModal
